/**
 * Build-time / env-only config. Anything Paul might want to edit lives in the
 * site-settings Payload global (see src/lib/site-settings.ts) with the env
 * value as the field's defaultValue. Only put things here that cannot live
 * in Payload because they need to be available at module load (analytics IDs,
 * site URL for metadata building, third-party site keys).
 */
export const siteUrl = process.env.NEXT_PUBLIC_SITE_URL ?? 'https://rocknearthworks.com.au'

/**
 * 'production', 'preview' or 'development', inlined by next.config from
 * VERCEL_ENV at build time. Anything but production is a build nobody
 * should be measured on.
 */
export const deployEnv = process.env.NEXT_PUBLIC_DEPLOY_ENV ?? 'development'

// Staff CRM, linked from the admin nav. Own-domain subdomain, so no referral UTMs.
export const crmUrl = 'https://crm.rocknearthworks.com.au'

// Every tracking id is empty anywhere but the production deployment. A preview
// once reported two synthetic leads into the live GA4 property because the id
// used to fall back to the real one. NEXT_PUBLIC_DEPLOY_ENV is inlined by
// next.config.mjs from VERCEL_ENV alone, so server and client agree and a stray
// NEXT_PUBLIC_VERCEL_ENV in the scope can't switch tracking on.
export const trackingEnabled = process.env.NEXT_PUBLIC_DEPLOY_ENV === 'production'

export const gaId = trackingEnabled ? (process.env.NEXT_PUBLIC_GA_ID ?? 'G-3J7YN5Z3VN') : ''
// Google Ads conversion tracking. Only .env.production sets it.
export const googleAdsId = trackingEnabled ? (process.env.NEXT_PUBLIC_GOOGLE_ADS_ID ?? '') : ''

// Social media URLs (used as defaults in the navigation global; kept here too
// because the navigation getter reads them at field-default time)
export const facebookUrl = process.env.NEXT_PUBLIC_FACEBOOK_URL ?? ''
export const instagramUrl = process.env.NEXT_PUBLIC_INSTAGRAM_URL ?? ''
export const tiktokUrl = process.env.NEXT_PUBLIC_TIKTOK_URL ?? ''
export const youtubeUrl = process.env.NEXT_PUBLIC_YOUTUBE_URL ?? ''
export const googleReviewsUrl = process.env.NEXT_PUBLIC_GOOGLE_REVIEWS_URL ?? ''

// Third-party integrations (build-time keys, never CMS-managed)
export const googleVerification = process.env.NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION ?? ''
export const metaPixelId = trackingEnabled ? (process.env.NEXT_PUBLIC_META_PIXEL_ID ?? '') : ''
export const tiktokPixelId = trackingEnabled ? (process.env.NEXT_PUBLIC_TIKTOK_PIXEL_ID ?? '') : ''

// The website chat (Rock'n's Chat, a Chatwoot inbox). The token is public by design and set per environment file; blank means no widget.
export const chatBaseUrl = 'https://chat.rocknearthworks.com.au'
export const chatWebsiteToken = process.env.NEXT_PUBLIC_CHATWOOT_WEBSITE_TOKEN ?? ''

// Not live until Gordon releases it (25 Oct 2026). Production never shows the chat, whatever the token or the admin switch says.
/**
 * Where the chat bubble shows (Gordon, 26 Oct 2026): home, contact and the pages that sell or support. The quote page is the contact page;
 * accepting a quote is selling. Never on the fun or editorial ones (/doof, reviews and testimonials, legal pages).
 */
export const CHAT_PAGES: readonly string[] = [
  '/',
  '/quote',
  '/services',
  '/rock-walls',
  '/builders',
  '/accept/*',
]

export function chatAllowed({
  vercelEnv,
  token,
  enabled,
}: {
  vercelEnv: string | undefined
  token: string
  enabled: boolean
}): boolean {
  return vercelEnv !== 'production' && token !== '' && enabled
}
